/**
 * Enterprise-Grade Workflow YAML Converter
 * Converts workflow nodes and edges to production YAML format
 * Compatible with BringIQ workflow execution engine
 */
import { Node, Edge } from 'reactflow';

// ============================================================================
// YAML Structure Types (snake_case for YAML output)
// ============================================================================

export interface WorkflowYAMLConfig {
  // Global workflow configuration
  enable_summarization_node: boolean;
  max_concurrency: number;
  recursion_limit: number;
  messages_limit_before_summarization?: number;
  tokens_limit_before_summarization: number;
  retry_policy: YAMLRetryPolicy;

  // Tools configuration
  tools?: YAMLTool[];

  // Assistants configuration
  assistants: YAMLAssistant[];

  // States configuration
  states: YAMLState[];

  // Orphaned (disconnected) states
  orphaned_states?: YAMLOrphanedState[];

  // Meta states for visual positioning
  meta_states: YAMLMetaState[];
}

export interface YAMLRetryPolicy {
  backoff_factor: number;
  initial_interval_seconds: number;
  max_attempts: number;
  max_interval_seconds: number;
}

export interface YAMLTool {
  id: string;
  name?: string;
  tool?: string;
  toolset?: string;
  description?: string;
  trace?: boolean;
  config?: Record<string, any>;
}

export interface YAMLAssistantTool {
  name: string;
}

export interface YAMLAssistant {
  id: string;
  assistant_id?: string;
  system_prompt?: string;
  model?: string;
  temperature?: number;
  limit_tool_output_tokens?: number;
  exclude_extra_context_tools?: boolean;
  tools?: YAMLAssistantTool[];
  mcp_servers?: YAMLMCPServer[];
}

export interface YAMLMCPServer {
  name: string;
  description: string;
  config: {
    command: string;
    args?: string[];
    env?: Record<string, string>;
  };
}

export interface YAMLState {
  id: string;
  assistant_id: string;
  task?: string;
  output_schema?: string;
  resolve_dynamic_values_in_prompt?: boolean;
  wait_for_user_confirmation?: boolean;
  next?: YAMLNextConfig;
}

export interface YAMLNextConfig {
  state_id?: string;
  iter_key?: string;
  meta_iter_state_id?: string;
  meta_next_state_id?: string;
  store_in_context?: boolean;
  include_in_llm_history?: boolean;
  condition?: YAMLCondition;
  switch?: YAMLSwitch;
}

export interface YAMLCondition {
  expression: string;
  then: string;
  otherwise: string;
}

export interface YAMLSwitch {
  cases: YAMLSwitchCase[];
  default?: string;
}

export interface YAMLSwitchCase {
  condition: string;
  state_id: string;
}

export interface YAMLOrphanedState {
  id: string;
  tool_id?: string;
  next: Record<string, any>;
}

export interface YAMLMetaState {
  id: string;
  type: string;
  is_connected: boolean;
  position: { x: number; y: number };
  measured?: { width: number; height: number };
  selected?: boolean;
  data?: Record<string, any>;
}

// ============================================================================
// Internal Types (camelCase)
// ============================================================================

export interface StateConfig {
  id: string;
  type: string;
  label: string;
  position: { x: number; y: number };
  measured?: { width: number; height: number };
  config: Record<string, any>;
}

export interface TransitionConfig {
  id: string;
  from: string;
  to: string;
  sourceHandle?: string;
  targetHandle?: string;
  condition?: string;
  label?: string;
}

export interface WorkflowMetadata {
  name: string;
  description?: string;
  authorId?: string;
  authorName?: string;
  organizationId?: string;
  projectId?: string;
}

export interface WorkflowGlobalConfig {
  enableSummarization: boolean;
  maxConcurrency: number;
  recursionLimit: number;
  messagesLimitBeforeSummarization: number;
  tokensLimitBeforeSummarization: number;
  retryPolicy: {
    backoffFactor: number;
    initialIntervalSeconds: number;
    maxAttempts: number;
    maxIntervalSeconds: number;
  };
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_WORKFLOW_CONFIG: WorkflowGlobalConfig = {
  enableSummarization: false,
  maxConcurrency: 1,
  recursionLimit: 300,
  messagesLimitBeforeSummarization: 30,
  tokensLimitBeforeSummarization: 3000000,
  retryPolicy: {
    backoffFactor: 5,
    initialIntervalSeconds: 5,
    maxAttempts: 9,
    maxIntervalSeconds: 50,
  },
};

// ============================================================================
// Node to State Conversion
// ============================================================================

/**
 * Convert React Flow nodes to workflow states
 */
export const nodesToStates = (nodes: Node[]): StateConfig[] => {
  return nodes.map(node => {
    // Access measured property safely (it exists on internal nodes)
    const measured = (node as any).measured;
    return {
      id: node.id,
      type: node.type || 'unknown',
      label: node.data?.label || node.data?.stateId || node.type || 'Unknown',
      position: node.position,
      measured: measured ? {
        width: typeof measured.width === 'number' ? measured.width : 256,
        height: typeof measured.height === 'number' ? measured.height : 67,
      } : undefined,
      config: extractNodeConfig(node),
    };
  });
};

/**
 * Extract node-specific configuration based on type
 */
const extractNodeConfig = (node: Node): Record<string, any> => {
  const data = node.data || {};
  const config: Record<string, any> = {};

  switch (node.type) {
    case 'start':
      // Start node has minimal config
      break;

    case 'end':
      // End node has minimal config
      break;

    case 'assistant':
      config.assistantId = data.assistantId;
      config.stateId = data.stateId || data.label;
      config.task = data.task;
      config.outputSchema = data.outputSchema;
      config.useVirtualAssistant = data.useVirtualAssistant ?? true;
      config.limitToolOutputTokens = data.limitToolOutputTokens;
      // Transition settings
      config.outputKey = data.outputKey;
      config.keepHistory = data.keepHistory ?? true;
      config.overrideTask = data.overrideTask ?? false;
      config.storeInContext = data.storeInContext ?? true;
      config.includeInLlmHistory = data.includeInLlmHistory ?? true;
      config.clearPriorMessages = data.clearPriorMessages ?? false;
      config.clearContextStore = data.clearContextStore || 'no';
      config.resetContextKeys = data.resetContextKeys;
      // State settings
      config.interruptBefore = data.interruptBefore ?? false;
      config.finishIteration = data.finishIteration ?? false;
      config.resolveDynamicValues = data.resolveDynamicValues ?? false;
      config.resultAsHumanMessage = data.resultAsHumanMessage ?? false;
      // Retry policy
      config.retryPolicy = {
        initial: data.retryInitial ?? 1,
        backoff: data.retryBackoff ?? 2,
        max: data.retryMax ?? 60,
        attempts: data.retryAttempts ?? 3,
      };
      break;

    case 'tool':
      config.stateId = data.stateId || data.label;
      config.toolId = data.toolId || data.selectedTool;
      config.selectedTool = data.selectedTool;
      config.toolResultPointer = data.toolResultPointer;
      config.enableTracing = data.enableTracing ?? false;
      config.resolveDynamicValuesInResponse = data.resolveDynamicValuesInResponse ?? false;
      config.task = data.task;
      config.outputKey = data.outputKey;
      config.storeInContext = data.storeInContext ?? true;
      // Retry policy
      config.retryPolicy = {
        initial: data.retryInitial ?? 1,
        backoff: data.retryBackoff ?? 2,
        max: data.retryMax ?? 60,
        attempts: data.retryAttempts ?? 3,
      };
      break;

    case 'iterator':
      config.iterKey = data.iterKey || data.next?.iter_key || 'items';
      config.children = data.children || [];
      config.next = data.next;
      break;

    case 'conditional':
      config.expression = data.expression || data.next?.condition?.expression || '';
      config.then = data.then || data.next?.condition?.then || '';
      config.otherwise = data.otherwise || data.next?.condition?.otherwise || '';
      config.iterKey = data.iterKey || data.next?.iter_key;
      config.next = data.next;
      break;

    case 'switch':
      config.cases = data.cases || data.next?.switch?.cases || [];
      config.default = data.default || data.next?.switch?.default;
      config.next = data.next;
      break;

    case 'custom':
    case 'transform':
      config.stateId = data.stateId || data.label;
      config.task = data.task;
      config.outputSchema = data.outputSchema;
      config.interruptBefore = data.interruptBefore ?? false;
      config.finishIteration = data.finishIteration ?? false;
      config.resolveDynamicValues = data.resolveDynamicValues ?? false;
      config.resultAsHumanMessage = data.resultAsHumanMessage ?? false;
      config.outputKey = data.outputKey;
      config.storeInContext = data.storeInContext ?? true;
      // Retry policy
      config.retryPolicy = {
        initial: data.retryInitial ?? 1,
        backoff: data.retryBackoff ?? 2,
        max: data.retryMax ?? 60,
        attempts: data.retryAttempts ?? 3,
      };
      break;

    case 'note':
      config.content = data.content;
      break;

    default:
      // Copy all data for unknown types
      Object.assign(config, data);
  }

  return config;
};

/**
 * Convert React Flow edges to workflow transitions
 */
export const edgesToTransitions = (edges: Edge[]): TransitionConfig[] => {
  return edges.map(edge => ({
    id: edge.id,
    from: edge.source,
    to: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
    targetHandle: edge.targetHandle || undefined,
    condition: edge.data?.condition,
    label: edge.label as string || undefined,
  }));
};

// ============================================================================
// Enterprise YAML Generation
// ============================================================================

/**
 * Determine if a node is connected (has incoming or outgoing edges)
 */
const isNodeConnected = (nodeId: string, edges: Edge[]): boolean => {
  return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
};

/**
 * Find the next state for a given node based on edges
 */
const findNextState = (
  nodeId: string, 
  nodeType: string,
  nodeData: Record<string, any>,
  edges: Edge[],
  nodes: Node[]
): YAMLNextConfig | undefined => {
  const outgoingEdges = edges.filter(edge => edge.source === nodeId);
  
  if (outgoingEdges.length === 0) {
    return undefined;
  }

  // For conditional nodes, find then/otherwise paths
  if (nodeType === 'conditional') {
    const thenEdge = outgoingEdges.find(e => e.sourceHandle === 'then' || e.label === 'then');
    const otherwiseEdge = outgoingEdges.find(e => e.sourceHandle === 'otherwise' || e.label === 'otherwise');
    
    return {
      condition: {
        expression: nodeData.expression || nodeData.next?.condition?.expression || '',
        then: thenEdge?.target || nodeData.then || nodeData.next?.condition?.then || '',
        otherwise: otherwiseEdge?.target || nodeData.otherwise || nodeData.next?.condition?.otherwise || '',
      },
      iter_key: nodeData.iterKey || nodeData.next?.iter_key,
      meta_next_state_id: nodeId,
    };
  }

  // For switch nodes, build cases
  if (nodeType === 'switch') {
    const cases = (nodeData.cases || nodeData.next?.switch?.cases || []).map((c: any, idx: number) => ({
      condition: c.condition || c.expression || '',
      state_id: c.stateId || c.state_id || outgoingEdges[idx]?.target || '',
    }));
    
    return {
      switch: {
        cases,
        default: nodeData.default || nodeData.next?.switch?.default,
      },
    };
  }

  // For iterator nodes
  if (nodeType === 'iterator') {
    const firstChild = outgoingEdges[0]?.target;
    return {
      state_id: firstChild,
      iter_key: nodeData.iterKey || nodeData.next?.iter_key || 'items',
      meta_iter_state_id: nodeId,
    };
  }

  // For regular nodes, find the next state
  const nextEdge = outgoingEdges[0];
  if (!nextEdge) return undefined;

  const result: YAMLNextConfig = {
    state_id: nextEdge.target,
  };

  // Add iterator key if present
  if (nodeData.iterKey || nodeData.next?.iter_key) {
    result.iter_key = nodeData.iterKey || nodeData.next?.iter_key;
  }

  // Add meta references
  if (nodeData.next?.meta_iter_state_id) {
    result.meta_iter_state_id = nodeData.next.meta_iter_state_id;
  }
  if (nodeData.next?.meta_next_state_id) {
    result.meta_next_state_id = nodeData.next.meta_next_state_id;
  }

  // Add context settings
  if (nodeData.storeInContext !== undefined) {
    result.store_in_context = nodeData.storeInContext;
  }
  if (nodeData.includeInLlmHistory !== undefined) {
    result.include_in_llm_history = nodeData.includeInLlmHistory;
  }

  return result;
};

/**
 * Extract unique assistants from nodes
 * Includes full assistant configuration with tools
 */
const extractAssistants = (nodes: Node[]): YAMLAssistant[] => {
  const assistantMap = new Map<string, YAMLAssistant>();
  
  nodes.forEach(node => {
    if (node.type === 'assistant' && node.data) {
      const assistantId = node.data.assistantId;
      const stateId = node.data.stateId || node.data.label || node.id;
      
      if (assistantId && !assistantMap.has(stateId)) {
        const assistant: YAMLAssistant = {
          id: stateId,
          assistant_id: assistantId,
          system_prompt: node.data.systemPrompt,
          model: node.data.model,
          temperature: node.data.temperature,
          limit_tool_output_tokens: node.data.limitToolOutputTokens,
        };
        
        // Add exclude_extra_context_tools if set
        if (node.data.excludeExtraContextTools !== undefined) {
          assistant.exclude_extra_context_tools = node.data.excludeExtraContextTools;
        }
        
        // Add tools array if present
        if (node.data.tools && Array.isArray(node.data.tools) && node.data.tools.length > 0) {
          assistant.tools = node.data.tools.map((tool: any) => ({
            name: typeof tool === 'string' ? tool : tool.name,
          }));
        }
        
        // Add MCP servers if present
        if (node.data.mcpServers && node.data.mcpServers.length > 0) {
          assistant.mcp_servers = node.data.mcpServers.map((server: any) => ({
            name: server.name,
            description: server.description,
            config: {
              command: server.command,
              args: server.args,
              env: server.env,
            },
          }));
        }
        
        assistantMap.set(stateId, assistant);
      }
    }
  });
  
  return Array.from(assistantMap.values());
};

/**
 * Generate states from assistant/tool nodes
 */
const generateStates = (nodes: Node[], edges: Edge[]): YAMLState[] => {
  const states: YAMLState[] = [];
  const stateNodeTypes = ['assistant', 'tool', 'custom', 'transform'];
  
  nodes.forEach(node => {
    if (!stateNodeTypes.includes(node.type || '')) return;
    if (!isNodeConnected(node.id, edges)) return;
    
    const stateId = node.data?.stateId || node.data?.label || node.id;
    const assistantId = node.data?.assistantId || stateId;
    
    const state: YAMLState = {
      id: stateId,
      assistant_id: node.type === 'tool' ? stateId : assistantId,
    };
    
    if (node.data?.task) {
      state.task = node.data.task;
    }
    
    if (node.data?.outputSchema) {
      state.output_schema = node.data.outputSchema;
    }
    
    if (node.data?.resolveDynamicValues) {
      state.resolve_dynamic_values_in_prompt = true;
    }
    
    // Add wait_for_user_confirmation if set (for approval gates)
    if (node.data?.interruptBefore || node.data?.waitForUserConfirmation) {
      state.wait_for_user_confirmation = true;
    }
    
    const nextConfig = findNextState(node.id, node.type || '', node.data || {}, edges, nodes);
    if (nextConfig) {
      state.next = nextConfig;
    } else {
      state.next = {};
    }
    
    // Add meta_iter_state_id if node is inside an iterator
    if (node.data?.parentIteratorId) {
      const iteratorId = node.data.parentIteratorId;
      // Find the iterator node to get its stateId
      const iteratorNode = nodes.find(n => n.id === iteratorId);
      if (iteratorNode) {
        state.next = state.next || {};
        state.next.meta_iter_state_id = iteratorNode.data?.stateId || iteratorNode.data?.label || iteratorId;
      }
    }
    
    states.push(state);
  });
  
  return states;
};

/**
 * Generate orphaned states (disconnected nodes)
 */
const generateOrphanedStates = (nodes: Node[], edges: Edge[]): YAMLOrphanedState[] => {
  const orphaned: YAMLOrphanedState[] = [];
  const orphanableTypes = ['tool', 'transform', 'custom'];
  
  nodes.forEach(node => {
    if (!orphanableTypes.includes(node.type || '')) return;
    if (isNodeConnected(node.id, edges)) return;
    
    orphaned.push({
      id: node.id,
      tool_id: node.type === 'tool' ? (node.data?.toolId || '') : undefined,
      next: {},
    });
  });
  
  return orphaned;
};

/**
 * Generate meta states for visual positioning
 */
const generateMetaStates = (nodes: Node[], edges: Edge[]): YAMLMetaState[] => {
  return nodes.map(node => {
    // Access measured property safely (it exists on internal nodes)
    const measured = (node as any).measured;
    
    // For iterators, use stored width/height or measured
    let width = 256;
    let height = 67;
    
    if (node.type === 'iterator') {
      width = node.data?.width || measured?.width || 300;
      height = node.data?.height || measured?.height || 200;
    } else if (measured) {
      width = typeof measured.width === 'number' ? measured.width : 256;
      height = typeof measured.height === 'number' ? measured.height : 67;
    }
    
    const metaState: YAMLMetaState = {
      id: node.data?.stateId || node.id,
      type: node.type || 'unknown',
      is_connected: isNodeConnected(node.id, edges),
      position: {
        x: node.position.x,
        y: node.position.y,
      },
      measured: { width, height },
      selected: node.selected || false,
    };
    
    // Add parentIteratorId reference if node is inside an iterator
    if (node.data?.parentIteratorId) {
      const iteratorId = node.data.parentIteratorId;
      const iteratorNode = nodes.find(n => n.id === iteratorId);
      if (iteratorNode) {
        (metaState as any).parent_iterator_id = iteratorNode.data?.stateId || iteratorNode.data?.label || iteratorId;
      }
    }
    
    // Add data for control nodes
    if (['conditional', 'switch', 'iterator'].includes(node.type || '')) {
      metaState.data = {
        next: buildNodeNextData(node),
      };
      
      // For iterator, add child node IDs
      if (node.type === 'iterator' && node.data?.childNodeIds) {
        metaState.data.child_node_ids = node.data.childNodeIds;
      }
    } else if (node.type === 'start' || node.type === 'end') {
      metaState.data = {};
    }
    
    return metaState;
  });
};

/**
 * Build next data for control nodes
 */
const buildNodeNextData = (node: Node): Record<string, any> => {
  const data = node.data || {};
  
  if (node.type === 'iterator') {
    return {
      iter_key: data.iterKey || data.next?.iter_key || 'items',
    };
  }
  
  if (node.type === 'conditional') {
    return {
      condition: {
        expression: data.expression || data.next?.condition?.expression || '',
        then: data.then || data.next?.condition?.then || '',
        otherwise: data.otherwise || data.next?.condition?.otherwise || '',
      },
      iter_key: data.iterKey || data.next?.iter_key,
    };
  }
  
  if (node.type === 'switch') {
    return {
      switch: {
        cases: (data.cases || data.next?.switch?.cases || []).map((c: any) => ({
          condition: c.condition || c.expression || 'x == true',
          state_id: c.stateId || c.state_id || '',
        })),
        default: data.default || data.next?.switch?.default || '',
      },
    };
  }
  
  return {};
};

/**
 * Convert workflow to enterprise-grade YAML string
 */
export const workflowToYAML = (
  nodes: Node[],
  edges: Edge[],
  metadata: WorkflowMetadata,
  globalConfig?: Partial<WorkflowGlobalConfig>
): string => {
  const config = { ...DEFAULT_WORKFLOW_CONFIG, ...globalConfig };
  
  const yamlConfig: WorkflowYAMLConfig = {
    // Global configuration
    enable_summarization_node: config.enableSummarization,
    max_concurrency: config.maxConcurrency,
    recursion_limit: config.recursionLimit,
    tokens_limit_before_summarization: config.tokensLimitBeforeSummarization,
    retry_policy: {
      backoff_factor: config.retryPolicy.backoffFactor,
      initial_interval_seconds: config.retryPolicy.initialIntervalSeconds,
      max_attempts: config.retryPolicy.maxAttempts,
      max_interval_seconds: config.retryPolicy.maxIntervalSeconds,
    },
    
    // Assistants
    assistants: extractAssistants(nodes),
    
    // States
    states: generateStates(nodes, edges),
    
    // Orphaned states
    orphaned_states: generateOrphanedStates(nodes, edges),
    
    // Meta states
    meta_states: generateMetaStates(nodes, edges),
  };
  
  // Add messages limit if summarization is enabled
  if (config.enableSummarization) {
    yamlConfig.messages_limit_before_summarization = config.messagesLimitBeforeSummarization;
  }
  
  // Remove orphaned_states if empty
  if (yamlConfig.orphaned_states?.length === 0) {
    delete yamlConfig.orphaned_states;
  }
  
  return toEnterpriseYAML(yamlConfig);
};

// ============================================================================
// YAML Serialization
// ============================================================================

/**
 * Convert object to YAML string with proper formatting
 */
const toEnterpriseYAML = (obj: WorkflowYAMLConfig): string => {
  let yaml = '';
  
  // Global config section
  yaml += `enable_summarization_node: ${obj.enable_summarization_node}\n`;
  yaml += `max_concurrency: ${obj.max_concurrency}\n`;
  yaml += `recursion_limit: ${obj.recursion_limit}\n`;
  
  if (obj.messages_limit_before_summarization !== undefined) {
    yaml += `messages_limit_before_summarization: ${obj.messages_limit_before_summarization}\n`;
  }
  
  yaml += `tokens_limit_before_summarization: ${obj.tokens_limit_before_summarization}\n`;
  
  // Retry policy
  yaml += `retry_policy:\n`;
  yaml += `  backoff_factor: ${obj.retry_policy.backoff_factor}\n`;
  yaml += `  initial_interval_seconds: ${obj.retry_policy.initial_interval_seconds}\n`;
  yaml += `  max_attempts: ${obj.retry_policy.max_attempts}\n`;
  yaml += `  max_interval_seconds: ${obj.retry_policy.max_interval_seconds}\n`;
  yaml += '\n';
  
  // Tools (if any - top-level tool definitions)
  if (obj.tools && obj.tools.length > 0) {
    yaml += `tools:\n`;
    obj.tools.forEach(tool => {
      yaml += `  - id: ${tool.id}\n`;
      if (tool.tool) {
        yaml += `    tool: ${tool.tool}\n`;
      }
      if (tool.toolset) {
        yaml += `    toolset: ${tool.toolset}\n`;
      }
      if (tool.name) {
        yaml += `    name: ${tool.name}\n`;
      }
      if (tool.description) {
        yaml += `    description: ${tool.description}\n`;
      }
      if (tool.trace !== undefined) {
        yaml += `    trace: ${tool.trace}\n`;
      }
    });
    yaml += '\n';
  }
  
  // Assistants
  yaml += `assistants:\n`;
  if (obj.assistants.length === 0) {
    yaml += `  []\n`;
  } else {
    obj.assistants.forEach(assistant => {
      // Check if this is a preconfigured assistant reference (has assistant_id like PRECONFIGURED:xxx)
      if (assistant.assistant_id) {
        yaml += `  - assistant_id: ${assistant.assistant_id}\n`;
        yaml += `    id: ${assistant.id}\n`;
      } else if (assistant.exclude_extra_context_tools !== undefined) {
        // Match sample format: exclude_extra_context_tools first, then id
        yaml += `  - exclude_extra_context_tools: ${assistant.exclude_extra_context_tools}\n`;
        yaml += `    id: ${assistant.id}\n`;
      } else {
        yaml += `  - id: ${assistant.id}\n`;
      }
      
      // System prompt with multiline (skip for preconfigured assistants)
      if (assistant.system_prompt && !assistant.assistant_id) {
        yaml += `    system_prompt: |\n`;
        assistant.system_prompt.split('\n').forEach(line => {
          yaml += `      ${line}\n`;
        });
      }
      
      // Model and temperature (skip for preconfigured assistants)
      if (assistant.model && !assistant.assistant_id) {
        yaml += `    model: ${assistant.model}\n`;
      }
      if (assistant.temperature !== undefined && !assistant.assistant_id) {
        yaml += `    temperature: ${assistant.temperature}\n`;
      }
      if (assistant.limit_tool_output_tokens && !assistant.assistant_id) {
        yaml += `    limit_tool_output_tokens: ${assistant.limit_tool_output_tokens}\n`;
      }
      
      // Tools array for this assistant
      if (assistant.tools && assistant.tools.length > 0) {
        yaml += `    tools:\n`;
        assistant.tools.forEach(tool => {
          yaml += `      - name: ${tool.name}\n`;
        });
      }
      
      // MCP servers
      if (assistant.mcp_servers && assistant.mcp_servers.length > 0) {
        yaml += `    mcp_servers:\n`;
        assistant.mcp_servers.forEach(server => {
          yaml += `      - name: ${server.name}\n`;
          yaml += `        description: ${server.description}\n`;
          yaml += `        config:\n`;
          yaml += `          command: ${server.config.command}\n`;
          if (server.config.args && server.config.args.length > 0) {
            yaml += `          args:\n`;
            server.config.args.forEach(arg => {
              yaml += `            - "${arg}"\n`;
            });
          }
          if (server.config.env && Object.keys(server.config.env).length > 0) {
            yaml += `          env:\n`;
            Object.entries(server.config.env).forEach(([key, value]) => {
              yaml += `            ${key}: "${value}"\n`;
            });
          }
        });
      }
    });
  }
  yaml += '\n';
  
  // States
  yaml += `states:\n`;
  if (obj.states.length === 0) {
    yaml += `  []\n`;
  } else {
    obj.states.forEach(state => {
      yaml += `  - assistant_id: ${state.assistant_id}\n`;
      yaml += `    id: ${state.id}\n`;
      if (state.next) {
        yaml += formatNextConfig(state.next, 4);
      }
      if (state.output_schema) {
        yaml += `    output_schema: |\n`;
        state.output_schema.split('\n').forEach(line => {
          yaml += `      ${line}\n`;
        });
      }
      if (state.resolve_dynamic_values_in_prompt) {
        yaml += `    resolve_dynamic_values_in_prompt: true\n`;
      }
      if (state.task) {
        yaml += `    task: |\n`;
        state.task.split('\n').forEach(line => {
          yaml += `      ${line}\n`;
        });
      }
      // Add wait_for_user_confirmation for approval gates
      if (state.wait_for_user_confirmation) {
        yaml += `    wait_for_user_confirmation: true\n`;
      }
    });
  }
  yaml += '\n';
  
  // Orphaned states
  if (obj.orphaned_states && obj.orphaned_states.length > 0) {
    yaml += `orphaned_states:\n`;
    obj.orphaned_states.forEach(orphan => {
      yaml += `  - id: ${orphan.id}\n`;
      if (orphan.tool_id !== undefined) {
        yaml += `    tool_id: '${orphan.tool_id}'\n`;
      }
      yaml += `    next: {}\n`;
    });
    yaml += '\n';
  }
  
  // Meta states
  yaml += `meta_states:\n`;
  obj.meta_states.forEach(meta => {
    yaml += `  - type: ${meta.type}\n`;
    yaml += `    is_connected: ${meta.is_connected}\n`;
    yaml += `    position:\n`;
    yaml += `      x: ${meta.position.x}\n`;
    yaml += `      y: ${meta.position.y}\n`;
    if (meta.measured) {
      yaml += `    measured:\n`;
      yaml += `      width: ${meta.measured.width}\n`;
      yaml += `      height: ${meta.measured.height}\n`;
    }
    yaml += `    id: ${meta.id}\n`;
    if (meta.selected !== undefined) {
      yaml += `    selected: ${meta.selected}\n`;
    }
    if (meta.data) {
      yaml += `    data:\n`;
      yaml += formatDataObject(meta.data, 6);
    }
  });
  
  return yaml;
};

/**
 * Format next configuration for YAML output
 */
const formatNextConfig = (next: YAMLNextConfig, indent: number): string => {
  const spaces = ' '.repeat(indent);
  let yaml = `${spaces}next:\n`;
  
  if (next.condition) {
    yaml += `${spaces}  condition:\n`;
    yaml += `${spaces}    expression: ${next.condition.expression}\n`;
    yaml += `${spaces}    otherwise: ${next.condition.otherwise}\n`;
    yaml += `${spaces}    then: ${next.condition.then}\n`;
  }
  
  if (next.switch) {
    yaml += `${spaces}  switch:\n`;
    yaml += `${spaces}    cases:\n`;
    next.switch.cases.forEach(c => {
      yaml += `${spaces}      - condition: ${c.condition}\n`;
      yaml += `${spaces}        state_id: ${c.state_id}\n`;
    });
    if (next.switch.default) {
      yaml += `${spaces}    default: ${next.switch.default}\n`;
    }
  }
  
  if (next.iter_key) {
    yaml += `${spaces}  iter_key: ${next.iter_key}\n`;
  }
  
  if (next.state_id) {
    yaml += `${spaces}  state_id: ${next.state_id}\n`;
  }
  
  if (next.meta_iter_state_id) {
    yaml += `${spaces}  meta_iter_state_id: ${next.meta_iter_state_id}\n`;
  }
  
  if (next.meta_next_state_id) {
    yaml += `${spaces}  meta_next_state_id: ${next.meta_next_state_id}\n`;
  }
  
  if (next.store_in_context !== undefined) {
    yaml += `${spaces}  store_in_context: ${next.store_in_context}\n`;
  }
  
  if (next.include_in_llm_history !== undefined) {
    yaml += `${spaces}  include_in_llm_history: ${next.include_in_llm_history}\n`;
  }
  
  return yaml;
};

/**
 * Format data object for meta states
 */
const formatDataObject = (data: Record<string, any>, indent: number): string => {
  const spaces = ' '.repeat(indent);
  let yaml = '';
  
  if (data.next) {
    yaml += `${spaces}next:\n`;
    
    if (data.next.iter_key) {
      yaml += `${spaces}  iter_key: ${data.next.iter_key}\n`;
    }
    
    if (data.next.condition) {
      yaml += `${spaces}  condition:\n`;
      yaml += `${spaces}    expression: ${data.next.condition.expression || "''"}\n`;
      yaml += `${spaces}    then: ${data.next.condition.then || "''"}\n`;
      yaml += `${spaces}    otherwise: ${data.next.condition.otherwise || "''"}\n`;
    }
    
    if (data.next.switch) {
      yaml += `${spaces}  switch:\n`;
      yaml += `${spaces}    cases:\n`;
      (data.next.switch.cases || []).forEach((c: any) => {
        yaml += `${spaces}      - condition: ${c.condition || 'x == true'}\n`;
        yaml += `${spaces}        state_id: ${c.state_id || "''"}\n`;
      });
      if (data.next.switch.default !== undefined) {
        yaml += `${spaces}    default: ${data.next.switch.default || "''"}\n`;
      }
    }
  } else if (Object.keys(data).length === 0) {
    yaml += `${spaces}{}\n`;
  }
  
  return yaml;
};

// ============================================================================
// YAML Parsing (Import)
// ============================================================================

/**
 * Parse YAML string back to workflow configuration
 * Note: For production, this should use js-yaml library
 */
export const parseYAMLToWorkflow = (yamlString: string): {
  nodes: Node[];
  edges: Edge[];
  config: WorkflowGlobalConfig;
} | null => {
  try {
    // This is a placeholder - in production, use js-yaml
    console.warn('YAML parsing requires js-yaml library. Using simplified parser.');
    
    // Basic YAML parsing would go here
    // For now, return null and let the backend handle YAML parsing
    return null;
  } catch (error) {
    console.error('Failed to parse YAML:', error);
    return null;
  }
};

/**
 * Convert workflow config back to React Flow nodes
 */
export const statesToNodes = (states: StateConfig[]): Node[] => {
  return states.map(state => ({
    id: state.id,
    type: state.type,
    position: state.position,
    measured: state.measured,
    data: {
      label: state.label,
      stateId: state.id,
      ...state.config,
    },
  }));
};

/**
 * Convert workflow transitions back to React Flow edges
 */
export const transitionsToEdges = (transitions: TransitionConfig[]): Edge[] => {
  return transitions.map(transition => ({
    id: transition.id,
    source: transition.from,
    target: transition.to,
    sourceHandle: transition.sourceHandle,
    targetHandle: transition.targetHandle,
    type: 'smoothstep',
    animated: true,
    label: transition.label,
    style: {
      strokeWidth: 2,
      stroke: '#6366f1',
      strokeDasharray: '5,5',
    },
    markerEnd: {
      type: 'arrowclosed' as any,
      color: '#6366f1',
    },
    data: {
      condition: transition.condition,
    },
  }));
};

/**
 * Export workflow as downloadable YAML file
 */
export const downloadWorkflowYAML = (
  nodes: Node[],
  edges: Edge[],
  metadata: WorkflowMetadata,
  globalConfig?: Partial<WorkflowGlobalConfig>
): void => {
  const yaml = workflowToYAML(nodes, edges, metadata, globalConfig);
  const blob = new Blob([yaml], { type: 'text/yaml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${metadata.name.toLowerCase().replace(/\s+/g, '-')}-workflow.yaml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Copy workflow YAML to clipboard
 */
export const copyWorkflowYAMLToClipboard = async (
  nodes: Node[],
  edges: Edge[],
  metadata: WorkflowMetadata,
  globalConfig?: Partial<WorkflowGlobalConfig>
): Promise<boolean> => {
  try {
    const yaml = workflowToYAML(nodes, edges, metadata, globalConfig);
    await navigator.clipboard.writeText(yaml);
    return true;
  } catch (error) {
    console.error('Failed to copy YAML to clipboard:', error);
    return false;
  }
};

export default {
  workflowToYAML,
  parseYAMLToWorkflow,
  nodesToStates,
  edgesToTransitions,
  statesToNodes,
  transitionsToEdges,
  downloadWorkflowYAML,
  copyWorkflowYAMLToClipboard,
};
